<script setup lang="ts">

import { ref } from "vue";
import { formatDate } from "@library/Utilities";
import type { ICarbucksProfile, ILinkedCard } from "@domain/Carbucks";
import PurpleCard from '@/assets/images/carbucks/card-purple.png';
import OrangeCard from '@/assets/images/carbucks/card-orange.png';
import GreenCard from '@/assets/images/carbucks/card-green.png';
import VisaLogo from '@/assets/images/carbucks/visa.png';
import MastercardLogo from '@/assets/images/carbucks/mastercard.png';
import AmexLogo from '@/assets/images/carbucks/amex.png';
import { useConfirm, useNotify } from "@library/Components";
import { useCarbucksApi } from "../Types/Api/CarbucksApi";
import LinkedCardExpiration from "./LinkedCardExpiration.vue";
import { useRouter } from "@library/Services";

const model = defineModel<ICarbucksProfile>( { required: true } );
const selectedCard = ref( 0 );
const cards = [ PurpleCard, OrangeCard, GreenCard ];
const logos: Record<string,any> = { visa: VisaLogo, mastercard: MastercardLogo, amex: AmexLogo };

const cardImage = ( index: number ) => cards[ index % cards.length ];
const logo = ( key: string ) => logos[ key ];

/**
 * Removes a linked card.
 */
 async function removeCard( card: ILinkedCard )
{
    const api = useCarbucksApi();
    const result = await api.removeLinkedCard( card.linkedCardId );
    if( result.success )
    {
        model.value.linkedCards = model.value.linkedCards.filter( c => c.linkedCardId != card.linkedCardId );
        return;
    }

    await useNotify( 'Unfortunately there was a problem and the card could not be removed.  Please try again later.' )
}

/**
 * Removes a linked card after a confirmation.
 */
 async function confirmAndRemoveCard( card: ILinkedCard )
{
    const isConfirmed = await useConfirm( 'By removing this card you accept that:<br/>Any pending Cashback affiliated to a transaction linked to this card might be lost, and;<br/>That any purchases made on this card will no longer be eligible for cashback.<br/>Are you sure you want to proceed?' )
    if( isConfirmed == false ) return;

    await removeCard( card );
}

/**
 * Removes a linked card and takes the user to the page for linking a new card.
 */
async function removeCardAndLinkAnother( card: ILinkedCard )
{
    await removeCard( card );

    const router = useRouter();
    router.go( model.value.getLinkCardUrl() );
}

</script>

<template>

    <div>

        <h1 class="text-base lg:text-lg 3xl:text-xl pt-6 pb-3 px-4
                   bg-gradient-to-b from-gray-850 to-gray-850/60 -mb-px">
            Linked Cards
        </h1>

        <div class="px-3 lg:px-4 py-5 gap-3 lg:gap-4
                    bg-gradient-to-b from-gray-850 to-gray-850/60">

            <v-slide-group v-if="model.hasLinkedCards && model.linkedCards.length > 0"
                           v-model="selectedCard"
                           :show-arrows="false"
                           class="-mx-3 lg:-mx-4"
                           center-active>

                <v-slide-group-item v-for="(card, index) in model.linkedCards" 
                                    :key="index"
                                    v-slot="{ isSelected, toggle }">

                    <div class="px-0 lg:px-2 first:!pl-4 last:!pr-4 lg:first:!pl-6 lg:last:!pr-6">
                        <div class="w-[295px] h-[174px] scale-90 lg:scale-100 transition-all duration-300">

                            <img :src="cardImage( index )" />

                            <img v-if="logo( card.issuer )"
                                 :src="logo( card.issuer )"
                                 class="absolute left-[5%] top-[9%] h-[8%]" />
                            <div v-else 
                                 class="absolute left-[7%] top-[7%] tracking-widest">
                                {{ card.provider }}
                            </div>

                            <div class="absolute right-[12%] top-[39%] tracking-widest">
                                {{ card.cardMasked }}
                            </div>

                            <div class="absolute left-[7%] bottom-[13%] text-xs">
                                {{ card.cardNickName ?? card.description }}
                            </div>

                            <div class="absolute right-[7%] bottom-[13%] text-xs text-right">
                                {{ formatDate( card.expiry, "MM/YY" )}}
                            </div>

                            <LinkedCardExpiration v-if="card.hasExpired" label="Expired" color="danger" @action="() => removeCardAndLinkAnother( card )">
                                Remove &amp; link new card ->
                            </LinkedCardExpiration>

                            <LinkedCardExpiration v-else-if="card.isExpiring" label="Expiring" color="warning" @action="() => removeCardAndLinkAnother( card )">
                                Remove &amp; link new card ->
                            </LinkedCardExpiration>

                            <CloseButton class="absolute top-2 right-2"
                                         title="Remove this card"
                                         @click="() => confirmAndRemoveCard( card )" />

                        </div>
                    </div>

                </v-slide-group-item>

            </v-slide-group>

            <div v-else 
                 class="flex flex-col w-full gap-2 tracking-wider
                        bg-gradient-to-b from-gray-850 to-gray-850/60">
                <p class="text-lg">
                    No cards have been linked
                </p>
                <p>
                    You need to link at least one card to receive cashback rewards
                </p>
                <p>
                    Tap LINK YOUR PAYMENT CARD to get started
                </p>
            </div>
        
            <div class="flex justify-center">
                <a :href="model.getLinkCardUrl()">
                    <Button variant="tonal"
                            type="button"
                            class="h-10 lg:!h-11 3xl:!h-12 rounded-lg mt-4 lg:mt-6">
                        {{ model.linkedCards.length > 0 ? 'Link another card' : 'Link your payment card' }}
                        <Icon name="arrow-right" class="ml-2 text-2xl" />
                    </Button>
                </a>
            </div>

        </div>

    </div>

</template>